import { Auth0Error } from 'auth0-js'
import * as runtypes from 'runtypes'
import { isAuth0Error } from '../auth'
import { logAndCaptureException } from '../monitoring'
import { RegistrationMessageActionType } from './types'

const CamelRestletErrorRunType = runtypes.Intersect(
  runtypes.Record({
    status: runtypes.Union(
      runtypes.Literal('bad request'),
      runtypes.Literal('unauthorized'),
      runtypes.Literal('forbidden'),
      runtypes.Literal('not found'),
      runtypes.Literal('method not allowed'),
      runtypes.Literal('conflict'),
      runtypes.Literal('internal server error'),
      runtypes.Literal('not implemented')
    ),
  }),
  runtypes.Partial({
    statusDetails: runtypes.Array(runtypes.String),
  })
)

const deferAfterTimeoutDetailsRunType = runtypes.Record({
  cursor: runtypes.String,
  timeout: runtypes.String,
})

export class RegistrationError extends Error {
  readonly name = 'RegistrationError'
  readonly actions?: RegistrationMessageActionType[]

  constructor(message?: string, actions?: RegistrationMessageActionType[]) {
    super(message)

    // avoid broken `instanceof`
    // ref: https://www.typescriptlang.org/docs/handbook/2/classes.html#inheriting-built-in-types
    Object.setPrototypeOf(this, RegistrationError.prototype)

    this.actions = actions
  }
}

export class DeferAfterTimeoutError extends Error {
  readonly name = 'DeferAfterTimeoutError'
  readonly cursor: string
  readonly timeout: string

  constructor({ cursor, timeout }: { cursor: string; timeout: string }) {
    super('Request deferred after timeout')

    Object.setPrototypeOf(this, DeferAfterTimeoutError.prototype)

    this.cursor = cursor
    this.timeout = timeout
  }
}

/**
 * Fallback error type for capturing non-Error objects.
 */
export class CoercedError extends Error {
  readonly name = 'CoercedError'

  constructor(readonly original: unknown) {
    super(`${original}`)

    Object.setPrototypeOf(this, CoercedError.prototype)
  }
}

/**
 * Wrap error object from Auth0 response in a custom error type for logging.
 */
class Auth0ResponseError extends Error {
  readonly name = 'Auth0ResponseError'

  constructor(readonly original: Auth0Error) {
    super(original.error)

    Object.setPrototypeOf(this, Auth0ResponseError.prototype)
  }
}

// errors returned by Auth0 Actions may include `friendly_message` prop
interface Auth0ActionsError extends Auth0Error {
  code: 'extensibility_error'
  original?: {
    response?: {
      body?: {
        friendly_message?: string
      }
    }
  }
}

function isAuth0ActionsError(value: Auth0Error): value is Auth0ActionsError {
  return value.code === 'extensibility_error'
}

const friendlyMessage = (value: Auth0ActionsError): string | undefined =>
  value.original?.response?.body?.friendly_message

// error types emitted by custom Auth0 Actions
const extensibilityErrorTypes = [
  'BAD_REQUEST',
  'CONFLICT',
  'DEFER_AFTER_TIMEOUT',
  'PUBLIC_API_ERROR',
  'UNEXPECTED_ERROR',
] as const

type ExtensibilityErrorType = typeof extensibilityErrorTypes[number]

interface ExtensibilityError {
  type: ExtensibilityErrorType
  details: unknown
}

function isExtensibilityErrorType(val: string): val is ExtensibilityErrorType {
  return (extensibilityErrorTypes as ReadonlyArray<string>).includes(val)
}

const extensibilityErrorRegex = /^([^|]+)\|Details:(.*)$/

export const parseExtensibilityError = (description: string): ExtensibilityError | undefined => {
  const match = description.match(extensibilityErrorRegex)
  if (!match) {
    return undefined
  }
  const [, type, detailsRaw] = match
  if (!isExtensibilityErrorType(type)) {
    console.error('[parseExtensibilityError] unexpected type:', type)
    return undefined
  }

  try {
    const details = JSON.parse(detailsRaw)
    return { type, details }
  } catch (parseErr) {
    console.error('[parseExtensibilityError] unable to parse details:', detailsRaw)
    return { type, details: detailsRaw }
  }
}

function reduceConflictErrorDetail(
  acc: { accountUnavailable: boolean; resetPasswordSent: boolean },
  statusDetail: string
): { accountUnavailable: boolean; resetPasswordSent: boolean } {
  if (statusDetail === 'account_unavailable') {
    return {
      ...acc,
      accountUnavailable: true,
    }
  }
  if (statusDetail === 'reset_password_sent') {
    return {
      ...acc,
      resetPasswordSent: true,
    }
  }
  return acc
}

function parseConflictErrorDetails(
  errorDetails: string[],
  enableGuestCheckout: boolean
): RegistrationError {
  const { accountUnavailable, resetPasswordSent } = errorDetails.reduce(reduceConflictErrorDetail, {
    accountUnavailable: false,
    resetPasswordSent: false,
  })
  if (accountUnavailable) {
    return new RegistrationError('Login unavailable for this email.')
  }
  if (resetPasswordSent) {
    return new RegistrationError(
      'It looks like you have an existing account with us. Please check your email to reset your password.',
      ['user_action_required']
    )
  }
  if (enableGuestCheckout) {
    return new RegistrationError(
      'It looks like you already have an account with us or have used Guest Checkout before.',
      ['reset_password']
    )
  }
  return new RegistrationError('It looks like you already have an account with us.', [
    'reset_password',
  ])
}

const badRequestPrefixRegex = /^Bad request:\s*/

function transformBadRequestMessage(message?: string): string | undefined {
  if (!message) {
    return message
  }
  const match = message.match(badRequestPrefixRegex)
  if (!match) {
    return message
  }
  // remove "Bad request:" prefix
  const details = message.slice(match[0].length)

  if (details === 'Practitioner code not recognized') {
    return 'Practitioner code not recognized. Please contact your practitioner for assistance'
  }
  if (details === 'ZIP Code currently not supported') {
    return 'The Zip Code entered is not supported'
  }
  return details
}

export const parseRegistrationError = (
  error: unknown,
  isGuestCheckoutAllowed = false
): RegistrationError | DeferAfterTimeoutError => {
  const weakPassword = 'The entered password is too weak.'
  const invalidPassword = 'Invalid password entered. Please check and try again.'
  const accountExists = 'It looks like you already have an account with us.'
  const accountExistsGuest =
    'It looks like you already have an account with us or have used Guest Checkout before.'
  const badRequestError = 'Please check your information and try again.'
  const internalError = 'An error occurred while attempting sign up. Please try again later.'
  if (!isAuth0Error(error)) {
    logAndCaptureException(error instanceof Error ? error : new CoercedError(error))
    return new RegistrationError(internalError)
  }
  if (error.code === 'invalid_password') {
    if (error.name === 'PasswordStrengthError') {
      const policySuffix = error.policy ? `\nPassword rules:\n${error.policy}` : ''
      return new RegistrationError(`${weakPassword}${policySuffix}`)
    }
    return new RegistrationError(invalidPassword)
  }
  if (error.code === 'invalid_signup') {
    if (error.name === 'BadRequestError') {
      if (isGuestCheckoutAllowed) {
        return new RegistrationError(accountExistsGuest, ['reset_password'])
      }
      return new RegistrationError(accountExists, ['reset_password'])
    }
    return new RegistrationError(badRequestError)
  }

  if (!isAuth0ActionsError(error) || !error.description) {
    logAndCaptureException(new Auth0ResponseError(error))
    return new RegistrationError(internalError)
  }

  const extensibilityError = parseExtensibilityError(error.description)
  if (!extensibilityError) {
    return new RegistrationError(internalError)
  }

  const { type, details } = extensibilityError

  if (type === 'DEFER_AFTER_TIMEOUT') {
    if (deferAfterTimeoutDetailsRunType.guard(details)) {
      return new DeferAfterTimeoutError(details)
    }
    logAndCaptureException(new Auth0ResponseError(error))
    return new RegistrationError(internalError)
  }

  const camelRestletErrorDetails = CamelRestletErrorRunType.guard(details) ? details : undefined

  // check for conflict first, to avoid using `friendly_message`
  if (type === 'CONFLICT') {
    return parseConflictErrorDetails(
      camelRestletErrorDetails?.statusDetails ?? [],
      isGuestCheckoutAllowed
    )
  }

  if (type === 'BAD_REQUEST') {
    // set email validation error message if present
    const detailsString = String(details)
    const emailErrorMessage = detailsString.toLowerCase().includes('email')
      ? `Invalid email: ${detailsString}`
      : ''
    // parse and use bad request error message from RESTlet if available
    const statusDetail = camelRestletErrorDetails
      ? transformBadRequestMessage(camelRestletErrorDetails.statusDetails?.[0])
      : undefined
    // fallback to friendly message from Auth0 Actions or generic bad request error
    return new RegistrationError(
      emailErrorMessage || statusDetail || friendlyMessage(error) || badRequestError
    )
  }
  if (
    type === 'PUBLIC_API_ERROR' &&
    (camelRestletErrorDetails?.statusDetails?.[0] === 'Invalid date value (must be M/D/YYYY)' ||
      camelRestletErrorDetails?.statusDetails?.[0] ===
        'Invalid date value (must be on or after 04/01/1601)')
  ) {
    return new RegistrationError('Invalid Expected Graduation Date. Please try again.')
  }

  logAndCaptureException(new Auth0ResponseError(error))
  return new RegistrationError(friendlyMessage(error) || internalError)
}
